import * as React from "react";
import { DEFAULT_KEYWORD_QUERY } from "../hooks/useNavigationState";
import { CultureMap, CultureMapData } from "./CultureMap";
import { Globe3DMap } from "./Globe3DMap";
import { CulturePicker } from "./CulturePicker";
import { TimelineScrubber, TimelineRange } from "./TimelineScrubber";
import { TimelineBucket, isFullRange } from "../../backend/lib/timeline";
import { trackEvent } from "../lib/analytics";

interface SearchHeroProps {
  // Timeline
  /** Counts per time bucket for the current query. */
  timelineBuckets: TimelineBucket[];
  /** Objects with a usable date for the current filters — a head count. */
  timelineTotal: number;
  timelineRange: TimelineRange;
  /** Fired on release — updates filters AND searches, like the map does. */
  onTimelineCommit: (range: TimelineRange) => void;
  timelineLoading?: boolean;
  keywordQuery: string;
  onKeywordQueryChange: (value: string) => void;
  onSearch: () => void;
  isCompact: boolean;
  // Culture picker
  allOptions: string[];
  selectedFilters: string[];
  onTogglePill: (option: string) => void;
  onClearFilters: () => void;
  // Inline map
  culturesData: CultureMapData[];
  culturesToHighlight: Set<string>;
  filterSearch: string;
  /** Updates filters AND immediately fires a search — no extra click needed. */
  onMapFilterToggle: (culture: CultureMapData) => void;
  /** When true, render Globe3DMap instead of the 2D CultureMap (behind ?globe=true flag). */
  enableGlobe?: boolean;
}

export const SearchHero: React.FC<SearchHeroProps> = ({
  keywordQuery,
  onKeywordQueryChange,
  onSearch,
  isCompact,
  allOptions,
  selectedFilters,
  onTogglePill,
  onClearFilters,
  culturesData,
  culturesToHighlight,
  filterSearch,
  onMapFilterToggle,
  enableGlobe = false,
  timelineBuckets,
  timelineTotal,
  timelineRange,
  onTimelineCommit,
  timelineLoading = false,
}) => {
  const [showMap, setShowMap] = React.useState(false);
  // Open by default once a range is set, so a filter restored from the URL is
  // never invisibly in force.
  const [showTimeline, setShowTimeline] = React.useState(
    () => !isFullRange(timelineRange.from, timelineRange.to),
  );
  const timelineActive = !isFullRange(timelineRange.from, timelineRange.to);

  const displayValue =
    keywordQuery === DEFAULT_KEYWORD_QUERY ? "" : keywordQuery;

  return (
    <div
      className={`search-hero bg-theme-color border-theme-color border ${
        isCompact ? "search-hero--compact" : "search-hero--expanded"
      }`}
    >
      <div className="search-hero-inner">
        {/* Row 1: Search input + action buttons */}
        <div className="search-hero-form">
          <label
            htmlFor="keyword-search-hero-input"
            className="search-hero-label font-bold px-2"
          >
            SEARCH:
          </label>
          <input
            id="keyword-search-hero-input"
            type="text"
            value={displayValue}
            onChange={(e) => onKeywordQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            placeholder="Find artifacts by keyword, culture, or country."
            className="search-hero-input border p-2"
          />
          <div className="flex flex-row-md flex-column">
            <button
              type="button"
              onClick={onSearch}
              className="border-theme-color text-theme-color border border-dashed hover:opacity-50"
            >
              <p className="font-bold cursor-pointer rotate-45 px-2" title="Search">
                ⚲
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                if (!showTimeline) trackEvent("timeline_open");
                setShowTimeline(!showTimeline);
              }}
              title={showTimeline ? "Hide timeline" : "Filter by time period"}
              aria-label={showTimeline ? "Hide timeline" : "Filter by time period"}
              aria-expanded={showTimeline}
              className={`border-theme-color text-theme-color border border-dashed hover:opacity-50 flex flex-row items-center justify-center gap-1 px-2 ${
                showTimeline ? "font-bold" : ""
              }`}
              style={
                timelineActive
                  ? {
                      color: "var(--accent-color)",
                      borderColor: "var(--accent-color)",
                    }
                  : undefined
              }
            >
              <span className="search-hero-btn-text">
                {showTimeline ? "HIDE TIME" : "TIME"}
              </span>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => {
                if (!showMap) trackEvent("map_open");
                setShowMap(!showMap);
              }}
              title={showMap ? "Hide map" : "Show culture map"}
              aria-label={showMap ? "Hide map" : "Show culture map"}
              aria-expanded={showMap}
              className={`border-theme-color text-theme-color border border-dashed hover:opacity-50 flex flex-row items-center justify-center gap-1 px-2 ${
                showMap ? "font-bold" : ""
              }`}
            >
              <span className="search-hero-btn-text">
                {showMap ? "HIDE MAP" : "MAP"}
              </span>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2: Culture picker pills */}
        <CulturePicker
          options={allOptions}
          selectedFilters={selectedFilters}
          keywordQuery={displayValue}
          onToggle={onTogglePill}
          onClearFilters={onClearFilters}
        />

        {/* Row 3: Timeline scrubber (toggled by TIME button) */}
        {showTimeline && (
          <TimelineScrubber
            buckets={timelineBuckets}
            total={timelineTotal}
            range={timelineRange}
            onRangeCommit={onTimelineCommit}
            loading={timelineLoading}
          />
        )}

        {/* Row 4: Inline map panel (toggled by MAP button) */}
        {showMap && (
          <div className="search-hero-map pt-1 z-0">
            {enableGlobe ? (
              <Globe3DMap
                culturesData={culturesData}
                culturesToHighlight={culturesToHighlight}
                onCultureToggle={onMapFilterToggle}
                height={300}
              />
            ) : (
              <CultureMap
                culturesData={culturesData}
                culturesToHighlight={culturesToHighlight}
                filterSearch={filterSearch}
                onCultureClick={onMapFilterToggle}
                height={300}
              />
            )}
            <p className="text-xs opacity-40 text-center pt-1 pb-1">
              {enableGlobe
                ? "Drag to rotate · Scroll to zoom · Click to filter"
                : "Click a pin to filter · Scroll to zoom"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
